import { Badge, Grid, GridColProps, GridProps, Text, TextProps } from '@mantine/core';
import { CollapsibleSection } from '@/app/components/shared/viewer/CollapsibleSection';
import { sumUndefinedNumbers } from '@/utils/sumUndefinedNumber';
import { useRawModel } from '../../hooks/useAssetsQuery';
import { PropsWithChildren } from 'react';
import { RapidModel } from '@/types/rapidModel';
import { RawModel } from '@/types/rawModel';
import { getSize } from '../../components/table/utils/getSize';
import { getFormattedNumber } from '../../components/table/utils/getFormattedNumber';

type OutputMetadataProps = {
  outputModel: RapidModel;
} & GridProps;

export const TextColumn = ({
  children,
  textProps,
  ...props
}: PropsWithChildren & GridColProps & { textProps?: TextProps }) => (
  <Grid.Col {...props}>
    <Text c='gray.6' size='sm' ta='center' {...textProps}>
      {children}
    </Text>
  </Grid.Col>
);

export const ResultColumn = ({
  children,
  span = 2,
  highlight = false,
  ...props
}: PropsWithChildren & GridColProps & { highlight?: boolean }) => (
  <Grid.Col span={span} {...props} ta='center'>
    <Badge
      py='2'
      px='xs'
      bg={highlight ? 'teal.2' : 'white'}
      c='dark'
      radius='md'
      fw='400'
      size='lg'
      component='span'
      m='auto'
    >
      {children}
    </Badge>
  </Grid.Col>
);

const calculateOutputMetadata = (outputModel: RapidModel, rawModel: RawModel) => {
  const outputSize = outputModel.meta?.size ?? 0;
  const outputFaces = sumUndefinedNumbers(outputModel.rpdInfo?.numTriangles, outputModel.rpdInfo?.numQuads);
  const outputTextures = sumUndefinedNumbers(
    outputModel.rpdInfo?.numTSNormalMaps,
    outputModel.rpdInfo?.numOSNormalMaps,
    outputModel.rpdInfo?.numBaseColorMaps,
    outputModel.rpdInfo?.numMetallicMaps,
    outputModel.rpdInfo?.numRoughnessMaps,
    outputModel.rpdInfo?.numOcclusionMaps,
    outputModel.rpdInfo?.numDisplacmentMaps,
    outputModel.rpdInfo?.numEmissiveMaps
  );

  const rawSize = rawModel?.size ?? 0;
  const rawFaces = sumUndefinedNumbers(rawModel?.rpdInfo?.numTriangles, rawModel?.rpdInfo?.numQuads);
  const rawTextures = sumUndefinedNumbers(
    rawModel?.rpdInfo?.numTSNormalMaps,
    rawModel?.rpdInfo?.numOSNormalMaps,
    rawModel?.rpdInfo?.numBaseColorMaps,
    rawModel?.rpdInfo?.numMetallicMaps,
    rawModel?.rpdInfo?.numRoughnessMaps,
    rawModel?.rpdInfo?.numOcclusionMaps,
    rawModel?.rpdInfo?.numDisplacmentMaps,
    rawModel?.rpdInfo?.numEmissiveMaps
  );

  return {
    outputSize,
    outputFaces,
    outputTextures,
    rawSize,
    rawFaces,
    rawTextures,
  };
};

export const OutputMetadata = ({ outputModel, ...gridProps }: OutputMetadataProps) => {
  const rawModel = useRawModel(outputModel.id);

  if (!rawModel) {
    return null;
  }

  const { outputSize, outputFaces, outputTextures, rawSize, rawFaces, rawTextures } = calculateOutputMetadata(
    outputModel,
    rawModel
  );

  return (
    <CollapsibleSection title='3D Metadata Comparison'>
      <Grid columns={10} {...gridProps} gutter='xs'>
        <TextColumn span={6}></TextColumn>
        <TextColumn span={2}>Input</TextColumn>
        <TextColumn span={2}>Output</TextColumn>

        <TextColumn textProps={{ ta: 'left' }} span={6}>
          File size:{' '}
        </TextColumn>
        <ResultColumn span={2} highlight={rawSize < outputSize}>
          {getSize(rawSize)}
        </ResultColumn>
        <ResultColumn span={2} highlight={rawSize > outputSize}>
          {getSize(outputSize)}
        </ResultColumn>

        <TextColumn textProps={{ ta: 'left' }} span={6}>
          Triangle count:{' '}
        </TextColumn>
        <ResultColumn span={2} highlight={rawFaces < outputFaces}>
          {getFormattedNumber(rawFaces)}
        </ResultColumn>
        <ResultColumn span={2} highlight={rawFaces > outputFaces}>
          {getFormattedNumber(outputFaces)}
        </ResultColumn>

        <TextColumn textProps={{ ta: 'left' }} span={6}>
          Texture count:{' '}
        </TextColumn>
        <ResultColumn span={2} highlight={rawTextures < outputTextures}>
          {rawTextures}
        </ResultColumn>
        <ResultColumn span={2} highlight={rawTextures > outputTextures}>
          {outputTextures}
        </ResultColumn>
      </Grid>
    </CollapsibleSection>
  );
};
